import React, { useState, useEffect, useRef } from 'react';
import { includes } from 'lodash';
import { Button, Checkbox, Form } from 'semantic-ui-react';
import { useCookies } from 'react-cookie';
import { Link, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';
import BodyClass from '@plone/volto/helpers/BodyClass/BodyClass';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { useClient } from '@plone/volto/hooks/client/useClient';
import { hideDSGVOBanner } from '@kitconcept/volto-dsgvo-banner/actions';
import useSettings from '@kitconcept/volto-dsgvo-banner/components/useSettings';
import Google from '@kitconcept/volto-dsgvo-banner/components/Banner/Google';
import Matomo from '@kitconcept/volto-dsgvo-banner/components/Banner/Matomo';
import CloseIcon from '../../customizations/components/theme/Icons/CloseIcon';
import { getCookiesPolicyPath } from '../../helpers/cookiesPolicyPath';
import useCsjDsgvoConsent from './useCsjDsgvoConsent';
import { cookieBannerMessages as messages } from './cookieBannerMessages';

// Varnish strips consent cookies and shares anonymous HTML, so the server always
// renders the consent banner. This runs in <head> before the body is painted and
// hides it for visitors who already answered; React drops it after hydration.
const HIDE_ANSWERED_BANNER = `(function(){try{if(/(?:^|;\\s*)confirm_cookies=1(?:;|$)/.test(document.cookie)){var s=document.createElement('style');s.textContent='.csj-cookie-banner--consent{display:none!important}';document.head.appendChild(s);}}catch(e){}})();`;

const CsjDsgvoBanner = (props) => {
  const isClient = useClient();
  const settings = useSettings();
  const intl = useIntl();
  const location = useLocation();
  const consent = useCsjDsgvoConsent();
  const [cookies] = useCookies();
  const [configureCookies, setConfigureCookies] = useState(false);
  const [expandedSections, setExpandedSections] = useState([]);
  const openedFromBlockRef = useRef(false);

  const modules = settings.modules;
  const showTechnicallyRequired = settings.showTechnicallyRequired;
  const showBanner = settings.showBanner;

  let privacy_url = settings.privacy_url;
  if (privacy_url && typeof privacy_url === 'object') {
    privacy_url = privacy_url[intl.locale];
  }
  if (!privacy_url) {
    privacy_url = getCookiesPolicyPath(location.pathname, intl.locale);
  }
  privacy_url = flattenToAppURL(privacy_url);

  const showConfirmModal =
    isClient && showBanner
      ? !Number(cookies.confirm_cookies) || props.show
      : props.show;

  // Server render and the hydration pass must match, so neither reads cookies.
  const showConsentBanner = isClient
    ? showConfirmModal && !Number(cookies.confirm_cookies)
    : showBanner;

  const finish = (action) => {
    action();
    props.hideDSGVOBanner();
    setConfigureCookies(false);
    openedFromBlockRef.current = false;
  };

  const handleClosePreferences = () => {
    if (openedFromBlockRef.current) {
      props.hideDSGVOBanner();
      openedFromBlockRef.current = false;
    }
    setConfigureCookies(false);
  };

  const openPreferencesFromConsent = () => {
    openedFromBlockRef.current = false;
    setConfigureCookies(true);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId],
    );
  };

  useEffect(() => {
    if (props.show && Number(cookies.confirm_cookies)) {
      openedFromBlockRef.current = true;
      setConfigureCookies(true);
    }
  }, [props.show, cookies.confirm_cookies]);

  useEffect(() => {
    if (!showConfirmModal) {
      return;
    }
    const id = configureCookies
      ? 'csj-cookie-preferences-title'
      : 'csj-cookie-banner-title';
    document.getElementById(id)?.focus();
  }, [showConfirmModal, configureCookies]);

  const renderAccordionSection = (sectionId, title, description, checkbox) => {
    const expanded = expandedSections.includes(sectionId);
    return (
      <div className="csj-cookie-banner__section" key={sectionId}>
        <div className="csj-cookie-banner__section-head">
          <button
            type="button"
            className="csj-cookie-banner__section-toggle"
            aria-expanded={expanded}
            onClick={() => toggleSection(sectionId)}
          >
            <span
              className={
                expanded
                  ? 'csj-cookie-banner__chevron csj-cookie-banner__chevron--open'
                  : 'csj-cookie-banner__chevron'
              }
              aria-hidden
            />
            <span className="csj-cookie-banner__section-title">{title}</span>
          </button>
          <div className="csj-cookie-banner__section-control">{checkbox}</div>
        </div>
        {expanded &&
          String(description)
            .split(/\n\n+/)
            .filter(Boolean)
            .map((paragraph, index) => (
              <p key={index} className="csj-cookie-banner__section-body">
                {paragraph}
              </p>
            ))}
      </div>
    );
  };

  const preferencesForm = (
    <>
      <div className="csj-cookie-banner__content csj-cookie-banner__content--preferences">
        <div className="csj-cookie-banner__header">
          <h2 id="csj-cookie-preferences-title" tabIndex={-1}>
            {intl.formatMessage(messages.preferencesTitle)}
          </h2>
          <button
            type="button"
            className="csj-cookie-banner__close"
            onClick={handleClosePreferences}
            aria-label={intl.formatMessage(messages.closePreferences)}
          >
            <CloseIcon size={20} />
          </button>
        </div>
        <p>{intl.formatMessage(messages.preferencesIntro)}</p>
        <Form className="csj-cookie-banner__form">
          {showTechnicallyRequired &&
            renderAccordionSection(
              'necessary',
              <>
                {intl.formatMessage(messages.necessaryTitle)}{' '}
                <span className="csj-cookie-banner__badge">
                  {intl.formatMessage(messages.necessaryBadge)}
                </span>
              </>,
              intl.formatMessage(messages.necessaryDescription),
              <Checkbox toggle checked disabled label=" " />,
            )}
          {includes(modules, 'tracking') &&
            renderAccordionSection(
              'tracking',
              intl.formatMessage(messages.trackingTitle),
              intl.formatMessage(messages.trackingDescription),
              <Checkbox
                toggle
                checked={consent.confirmTracking}
                onChange={() =>
                  consent.setConfirmTracking(!consent.confirmTracking)
                }
              />,
            )}
          {includes(modules, 'facebook') && (
            <>
              {renderAccordionSection(
                'marketing',
                intl.formatMessage(messages.marketingTitle),
                intl.formatMessage(messages.marketingDescription),
                <Checkbox
                  toggle
                  checked={consent.confirmFacebook}
                  onChange={() =>
                    consent.setConfirmFacebook(!consent.confirmFacebook)
                  }
                />,
              )}
              {renderAccordionSection(
                'behavioral',
                intl.formatMessage(messages.behavioralTitle),
                intl.formatMessage(messages.behavioralDescription),
                <Checkbox
                  toggle
                  checked={consent.confirmBehavioralMarketing}
                  onChange={() =>
                    consent.setConfirmBehavioralMarketing(
                      !consent.confirmBehavioralMarketing,
                    )
                  }
                />,
              )}
            </>
          )}
        </Form>
      </div>
      <div className="csj-cookie-banner__actions csj-cookie-banner__actions--preferences">
        <div className="csj-cookie-banner__actions-primary">
          <Button
            type="button"
            className="csj-cookie-banner__btn csj-cookie-banner__btn--primary"
            onClick={() => finish(consent.rejectAll)}
          >
            {intl.formatMessage(messages.rejectAll)}
          </Button>
          <Button
            type="button"
            className="csj-cookie-banner__btn csj-cookie-banner__btn--primary"
            onClick={() => finish(consent.confirmAll)}
          >
            {intl.formatMessage(messages.acceptAll)}
          </Button>
        </div>
        <Button
          type="button"
          className="csj-cookie-banner__btn csj-cookie-banner__btn--primary"
          onClick={() => finish(consent.confirmSelection)}
        >
          {intl.formatMessage(messages.savePreferences)}
        </Button>
      </div>
    </>
  );

  return (
    <>
      {showBanner && (
        <Helmet>
          <script>{HIDE_ANSWERED_BANNER}</script>
        </Helmet>
      )}
      {isClient && settings.tracker?.type === 'google' && <Google />}
      {isClient && settings.tracker?.type === 'matomo' && <Matomo />}
      {configureCookies && showConfirmModal && (
        <BodyClass className="openCookiePreferences" />
      )}
      {showConsentBanner && (
        <div
          className="csj-cookie-banner csj-cookie-banner--consent"
          role="region"
          aria-labelledby="csj-cookie-banner-title"
          aria-hidden={configureCookies || undefined}
        >
          <div className="csj-cookie-banner__content">
            <h2 id="csj-cookie-banner-title" tabIndex={-1}>
              {intl.formatMessage(messages.consentTitle)}
            </h2>
            <p className="csj-cookie-banner__text">
              <FormattedMessage
                {...messages.consentDescription}
                values={{
                  cookiesLink: (
                    <Link to={privacy_url} className="csj-cookie-banner__link">
                      {intl.formatMessage(messages.cookiesPolicyLink)}
                    </Link>
                  ),
                }}
              />
            </p>
          </div>
          <div className="csj-cookie-banner__actions">
            <Button
              type="button"
              className="csj-cookie-banner__btn csj-cookie-banner__btn--primary"
              onClick={() => finish(consent.confirmAll)}
            >
              {intl.formatMessage(messages.acceptAll)}
            </Button>
            <Button
              type="button"
              className="csj-cookie-banner__btn csj-cookie-banner__btn--primary"
              onClick={() => finish(consent.rejectAll)}
            >
              {intl.formatMessage(messages.rejectAll)}
            </Button>
            {modules.length > 0 && (
              <Button
                type="button"
                className="csj-cookie-banner__btn csj-cookie-banner__btn--primary"
                onClick={openPreferencesFromConsent}
              >
                {intl.formatMessage(messages.configure)}
              </Button>
            )}
          </div>
        </div>
      )}
      {showConfirmModal && configureCookies && (
        <div
          className="csj-cookie-banner-preferences-overlay"
          aria-hidden="true"
        />
      )}
      {showConfirmModal && configureCookies && (
        <div
          className="csj-cookie-banner csj-cookie-banner--preferences"
          role="dialog"
          aria-modal="true"
          aria-labelledby="csj-cookie-preferences-title"
        >
          {preferencesForm}
        </div>
      )}
    </>
  );
};

export default connect(
  (state) => ({
    show: state.DSGVOBanner?.show,
  }),
  { hideDSGVOBanner },
)(CsjDsgvoBanner);
